import { doc, getDoc, getDocs, updateDoc, collection, query, where } from "firebase/firestore";
import { firestore, auth } from "../../firebaseConfig";
import { completeFlowStep } from "../warmupFlowUtils";

/**
 * Next-step actions one team member can hand to another from the Notes modal.
 * Saved to `World/{ownerId}/tasks`; open ones are mirrored onto the contact as
 * `open_task_assignees` so the table can filter and flag them server-side.
 */
export const TASK_ACTIONS = [
  { id: "send_mail", label: "Send mail", tagline: "Strike while it's hot", icon: "fa-paper-plane", defaultMessage: "Please send a mail with follow-up infos." },
  { id: "call_back", label: "Call back", tagline: "Personal touch wins", icon: "fa-phone", defaultMessage: "Please call the contact back." },
  { id: "book_meeting", label: "Book meeting", tagline: "Let's close this", icon: "fa-calendar-check", defaultMessage: "Please schedule a meeting." },
  { id: "other", label: "Other", tagline: "Custom mission", icon: "fa-wand-magic-sparkles", defaultMessage: "" },
];

export const getTaskAction = (id) => TASK_ACTIONS.find(a => a.id === id) || null;

export const nameFromEmail = (email) => {
  const local = (email || "").split("@")[0];
  if (!local) return "";
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1).replace(/\d+$/, ""))
    .filter(Boolean)
    .join(" ");
};

export const firstName = (name) => (name || "").split(/\s+/)[0] || name;

export const initials = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join("") || "?";

const AVATAR_GRADIENTS = [
  ["#6da7ed", "#2a6776"],
  ["#f6a57a", "#e5687d"],
  ["#8fd3a7", "#2f9e7a"],
  ["#b79cf2", "#6d5bd0"],
  ["#f7cf6b", "#e0913a"],
  ["#7fd1e3", "#3a86b7"],
];

export const avatarGradient = (seed) => {
  let h = 0;
  for (const ch of seed || "") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const [a, b] = AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
};

const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d;
};

export const formatDue = (ts) => {
  const d = new Date(ts);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const time = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  if (sameDay(d, new Date())) return `Today, ${time}`;
  if (sameDay(d, addDays(1))) return `Tomorrow, ${time}`;
  if (sameDay(d, addDays(-1))) return `Yesterday, ${time}`;
  return `${d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })}, ${time}`;
};

export const formatOpenSince = (ts) => {
  const mins = Math.max(0, Math.floor((Date.now() - ts) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"}`;
};

/** Accepted team members of a world, plus the signed-in user if missing. */
export const loadTeamMembers = async (ownerId) => {
  const me = auth.currentUser;
  const snap = await getDoc(doc(firestore, "teams", ownerId));
  const raw = snap.exists() ? snap.data().members || {} : {};
  const members = Object.entries(raw)
    .filter(([, m]) => (m?.status || "accepted") === "accepted")
    .map(([uid, m]) => {
      const isSelf = uid === me?.uid;
      const name = m.name || m.displayName || m.firstName || (isSelf ? me?.displayName : null)
        || nameFromEmail(m.email) || m.email || "Team member";
      const photoURL = m.photoURL || (isSelf ? me?.photoURL : null) || null;
      return { uid, email: m.email || "", name, isSelf, photoURL };
    });
  if (me && !members.some(m => m.uid === me.uid)) {
    members.unshift({ uid: me.uid, email: me.email || "", name: me.displayName || nameFromEmail(me.email) || me.email || "Me", isSelf: true, photoURL: me.photoURL || null });
  }
  members.sort((a, b) => (b.isSelf ? 1 : 0) - (a.isSelf ? 1 : 0));
  return members;
};

/** Assignee list of a task, tolerating the early single-assignee shape. */
export const taskAssigneeRefs = (task) => {
  if (Array.isArray(task?.assignees) && task.assignees.length) return task.assignees;
  if (task?.assigneeUid) return [{ uid: task.assigneeUid, email: task.assigneeEmail || null }];
  return [];
};

export const taskAssigneeUids = (task) =>
  Array.isArray(task?.assigneeUids) && task.assigneeUids.length
    ? task.assigneeUids
    : taskAssigneeRefs(task).map(a => a.uid);

/**
 * Recomputes `open_task_assignees` on the contact from its remaining open
 * tasks. Returns the new array so callers can patch local state.
 */
export const syncContactOpenAssignees = async (ownerId, contactId) => {
  const snap = await getDocs(query(
    collection(firestore, `World/${ownerId}/tasks`),
    where("contactId", "==", contactId),
    where("status", "==", "open"),
  ));
  const uids = [...new Set(snap.docs.flatMap(d => taskAssigneeUids(d.data())))];
  await updateDoc(doc(firestore, `World/${ownerId}/db/${contactId}`), { open_task_assignees: uids });
  return uids;
};

export const completeTask = async (ownerId, task, extra = {}) => {
  const me = auth.currentUser;
  await updateDoc(doc(firestore, `World/${ownerId}/tasks/${task.id}`), {
    status: "done",
    doneAt: Date.now(),
    doneByUid: me?.uid || null,
    doneByEmail: me?.email || null,
    ...extra,
  });
  return task.contactId ? syncContactOpenAssignees(ownerId, task.contactId) : null;
};

/**
 * Completes the flow step a task stood for (see taskFlowLink), hopping a
 * ready delay in front of it. Returns the engine result of the last hop.
 */
export const advanceLinkedFlow = async (ownerId, contactId, link) => {
  let idx = link.fromIndex;
  let result = null;
  for (let hops = 0; hops < 4; hops++) {
    result = await completeFlowStep(ownerId, contactId, link.flow, idx, "warmup", null, { force: true });
    if (!result.success || result.isComplete || idx === link.stepIndex) break;
    idx = result.newFlowState?.currentStepIndex;
    if (idx == null) break;
  }
  return result;
};

/** The task note as an instruction for the AI mail writer. */
export const taskBriefing = (task, fromName) => {
  const brief = (task?.message || "").trim();
  if (!brief || brief === getTaskAction(task.action)?.label) return "";
  return `Internal briefing from ${fromName || "a teammate"} for this mail. Act on it, never quote it: "${brief}"`;
};

// ── Cross-view shortcuts ────────────────────────────────────────────
// The Tasks modal lives in the header, but the actions it triggers belong to
// the Targets table (AI mail, templates, notes) or the Inbox (new mail,
// forward). A request is stashed in sessionStorage AND broadcast as an event:
// an already-mounted view handles the event, a view that mounts because of the
// navigation picks the stash up on mount. Whoever handles it clears it.

export const TASK_ACTION_EVENT = "bc:task-action";
/** Broadcast `{ contactId, updates }` so a mounted table can patch its row. */
export const CONTACT_PATCH_EVENT = "bc:contact-patched";
const PENDING_KEY = "bc_pending_task_action";

export const TABLE_TASK_ACTIONS = ["ai_mail", "template_mail", "open_notes"];
export const INBOX_TASK_ACTIONS = ["new_mail", "reply_thread", "forward_thread"];

export const requestTaskAction = (payload) => {
  const entry = { ...payload, at: Date.now() };
  try { sessionStorage.setItem(PENDING_KEY, JSON.stringify(entry)); } catch (_) { /* storage unavailable */ }
  // Defer so a view mounted by the same navigation has its listener attached.
  setTimeout(() => window.dispatchEvent(new CustomEvent(TASK_ACTION_EVENT, { detail: entry })), 0);
};

/** Returns and clears the stashed request if it is one of `kinds` and fresh. */
export const takePendingTaskAction = (kinds) => {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!kinds.includes(entry.kind) || Date.now() - (entry.at || 0) > 60000) return null;
    sessionStorage.removeItem(PENDING_KEY);
    return entry;
  } catch (_) {
    return null;
  }
};
